/**
 * A program's starts: when it runs each day, and how long each run is.
 *
 * Mirrors HHNodeServer/src/utils/programStarts.js, which stays authoritative —
 * the server refuses a set of starts this would have allowed. This copy exists
 * so the builder can say what a start will do while somebody is still typing,
 * and it pins the same cases (programStarts.test.ts / programStarts.test.js).
 * A change to one that does not break the other is the bug.
 */

import type { ProgramStart, ScheduleProgram, ScheduleStep } from '../services/espnowIrrigation';

export const DAY_SEC = 86400;
export const MIN_DURATION_PCT = 10;
export const MAX_DURATION_PCT = 300;
export const MAX_STARTS = 8;
/** Clear time the server wants between the end of one run and the next start. */
export const MIN_START_GAP_SEC = 5 * 60;

const wrapDay = (sec: number) => ((Math.round(Number(sec) || 0) % DAY_SEC) + DAY_SEC) % DAY_SEC;

/**
 * The program's starts, earliest first. A program from a server that predates
 * starts is its own start_time_sec at full length, which is what it meant.
 */
export function startsOf(program: Pick<ScheduleProgram, 'start_time_sec' | 'starts'>): ProgramStart[] {
  const starts = (program.starts ?? []).filter(Boolean);
  if (starts.length === 0) {
    return [{ id: null, start_time_sec: wrapDay(program.start_time_sec), duration_pct: 100 }];
  }
  return starts
    .map(s => ({
      id: s.id ?? null,
      start_time_sec: wrapDay(s.start_time_sec),
      duration_pct: Number(s.duration_pct) || 100,
    }))
    .sort((a, b) => a.start_time_sec - b.start_time_sec);
}

/** One duration at a percentage. Never rounds a running valve down to nothing. */
export function scaleSec(sec: number, pct: number): number {
  const s = Number(sec) || 0;
  if (s <= 0) return 0;
  const p = Number(pct) || 100;
  if (p === 100) return s;
  return Math.max(1, Math.round((s * p) / 100));
}

/** How long one run lasts at a percentage: the sum of its scaled steps. */
export function cycleSec(steps: ScheduleStep[] | null | undefined, pct = 100): number {
  return (steps ?? []).reduce((sum, s) => sum + scaleSec(s.step_duration_sec, pct), 0);
}

/** The program as it runs from one start: moved, and every duration scaled. */
export function programForStart(program: ScheduleProgram, start: ProgramStart): ScheduleProgram {
  const pct = Number(start.duration_pct) || 100;
  return {
    ...program,
    start_time_sec: wrapDay(start.start_time_sec),
    starts: [start],
    steps: (program.steps ?? []).map(step => ({
      ...step,
      step_duration_sec: scaleSec(step.step_duration_sec, pct),
      relays: step.relays == null ? step.relays : step.relays.map(r => ({
        ...r,
        duration_sec: scaleSec(r.duration_sec, pct),
      })),
    })),
  };
}

/** One program per start, earliest first. */
export function programRuns(program: ScheduleProgram): ScheduleProgram[] {
  return startsOf(program).map(start => programForStart(program, start));
}

export interface StartOverlap {
  start: ProgramStart;
  next: ProgramStart;
  /** How long `start`'s run lasts. */
  cycleSec: number;
  /** Time from `start` to `next`. */
  gapSec: number;
}

/**
 * Starts that begin before the previous run has finished, or less than
 * MIN_START_GAP_SEC after it. Circular: a late start that runs past midnight
 * meets the first start of the next day.
 */
export function findStartOverlaps(
  starts: ProgramStart[],
  steps: ScheduleStep[] | null | undefined,
): StartOverlap[] {
  const sorted = startsOf({ start_time_sec: 0, starts });
  if (sorted.length < 2) return [];
  const overlaps: StartOverlap[] = [];
  sorted.forEach((start, i) => {
    const next = sorted[(i + 1) % sorted.length];
    const gap = (next.start_time_sec - start.start_time_sec + DAY_SEC) % DAY_SEC || DAY_SEC;
    const runs = cycleSec(steps, start.duration_pct);
    if (runs + MIN_START_GAP_SEC > gap) {
      overlaps.push({ start, next, cycleSec: runs, gapSec: gap });
    }
  });
  return overlaps;
}
